import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Truck, Shield, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { products } from '../mock';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  // Featured products: Pure Ghee 1L, Cow Ghee 500ml, Rasgulla, Dairy Whitener 200g
  const featuredProductIds = ["4", "9", "19", "23"];
  const featuredProducts = products.filter(p => featuredProductIds.includes(p.id));
  const [heroSettings, setHeroSettings] = useState({
    heroImage: '/heroimg.png',
    heroImagePosition: 'center',
    heroImageZoom: 1.0
  });

  useEffect(() => {
    fetchHeroSettings();
  }, []);

  const fetchHeroSettings = async () => {
  try {
    const response = await axios.get(`${BACKEND_URL}/api/settings`);
    setHeroSettings({
      heroImage: '/heroimg.png',
      ...response.data
    });
  } catch (error) {
    console.error('Error fetching hero settings:', error);
    // Keep the default image if API fails
  }
};

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src={heroSettings.heroImage}
            alt="Arihant Dairy Factory"
            className="w-full h-full"
            style={{
              objectFit: 'cover',
              objectPosition: heroSettings.heroImagePosition,
              transform: `scale(${heroSettings.heroImageZoom})`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Pure. Natural. Authentic Dairy Products
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Experience the richness of traditional dairy products made with love and care at our state-of-the-art modern dairy plant.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button size="lg" className="bg-rose-500 hover:bg-rose-600 text-white">
                  Shop Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/mission">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white/20">
                  Our Story
                </Button>
              </Link>
              <Link to="/exports">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white/20">
                  <Globe className="mr-2 h-5 w-5" />
                  Exports
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Factory Video Section */}
      <section className="bg-gradient-to-r from-red-600 to-rose-600 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a 
            href="https://youtu.be/aFWFqtGOnHA?si=aZnrIOmPAv1STc3T"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-6 text-white hover:opacity-90 transition-opacity duration-200"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-full p-3 shadow-lg">
                <svg className="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <div className="text-left">
                <p className="text-2xl font-bold">Click here to visit our ultra-modern dairy plant</p>
                <p className="text-lg text-white/90">India's Most Advanced Dairy Plant with Latest Technology</p>
              </div>
            </div>
            <ArrowRight className="h-8 w-8 animate-pulse" />
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                <Award className="h-8 w-8 text-rose-600" />
              </div>
              <h3 className="text-xl font-semibold text-rose-900 mb-2">Premium Quality</h3>
              <p className="text-gray-600">
                100% pure and authentic products with no additives or preservatives
              </p>
            </div>

            <div className="flex flex-col items-center text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                <Truck className="h-8 w-8 text-rose-600" />
              </div>
              <h3 className="text-xl font-semibold text-rose-900 mb-2">Fast Delivery</h3>
              <p className="text-gray-600">
                Quick and reliable delivery to ensure freshness at your doorstep
              </p>
            </div>

            <div className="flex flex-col items-center text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-rose-600" />
              </div>
              <h3 className="text-xl font-semibold text-rose-900 mb-2">Certified Safe</h3>
              <p className="text-gray-600">
                All products meet international quality and safety standards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-rose-900 mb-4">Featured Products</h2>
            <p className="text-gray-600 text-lg">Discover our most loved dairy products</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map(product => (
              <div
                key={product.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-rose-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-rose-600">₹{product.price}</span>
                    <span className="text-sm text-gray-500">{product.weight}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-rose-500 hover:bg-rose-600 text-white">
                View All Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-rose-500 to-red-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Experience the Tradition of Pure Dairy
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of happy customers who trust us for their daily dairy needs
          </p>
          <Link to="/products">
            <Button size="lg" className="bg-white text-rose-600 hover:bg-gray-100">
              Start Shopping
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Our Certifications</h2>
            <p className="text-gray-600">Trusted quality standards and certifications</p>
          </div>
          
          {/* Desktop Layout - Side by Side */}
          <div className="hidden lg:flex justify-center items-center gap-8">
            {/* Left Mascot Image */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_selected-repo/artifacts/futnvvkh_IMG_1188.PNG"
                alt="Arihant Mascot"
                className="w-64 h-auto"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                  background: 'transparent'
                }}
              />
            </div>
            
            {/* Certifications Center */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_f5e3540e-97a9-470e-8a92-ab500e1eb8c7/artifacts/m6o1q6t0_our%20certifications.png"
                alt="Our Certifications - ISO 22000, FSSAI, Arihant, ISO 9001:2015"
                className="max-w-2xl w-full h-auto"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                  background: 'transparent'
                }}
              />
            </div>
            
            {/* Right Mascot Image */}
            <div className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_selected-repo/artifacts/gq4wbvnh_IMG_9400.PNG"
                alt="Arihant Mascot"
                className="w-64 h-auto"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                  background: 'transparent'
                }}
              />
            </div>
          </div>
          
          {/* Mobile Layout - Vertical Stack */}
          <div className="lg:hidden flex flex-col items-center gap-6">
            {/* Top Mascot Image */}
            <div className="w-48">
              <img 
                src="https://customer-assets.emergentagent.com/job_selected-repo/artifacts/futnvvkh_IMG_1188.PNG"
                alt="Arihant Mascot"
                className="w-full h-auto"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                  background: 'transparent'
                }}
              />
            </div>
            
            {/* Certifications Center */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_f5e3540e-97a9-470e-8a92-ab500e1eb8c7/artifacts/m6o1q6t0_our%20certifications.png"
                alt="Our Certifications - ISO 22000, FSSAI, Arihant, ISO 9001:2015"
                className="w-full h-auto"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                  background: 'transparent'
                }}
              />
            </div>
            
            {/* Bottom Mascot Image */}
            <div className="w-48">
              <img 
                src="https://customer-assets.emergentagent.com/job_selected-repo/artifacts/gq4wbvnh_IMG_9400.PNG"
                alt="Arihant Mascot"
                className="w-full h-auto"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                  background: 'transparent'
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
